import { ChevronLeftIcon, Share2Icon } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useGetChat, useGetDoclist } from "~/api/chat/chat";
import { useGetProject } from "~/api/projects/projects";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { useChatDocumentParams } from "~/helpers";
import { Button } from "../../../../components/ui/button";
import { CaseInfoPopover } from "../modals/CaseInfoPopover";
import { ShareCaseDialog } from "../modals/ShareCaseDialog";
import { PresenceAvatars } from "../presence/PresenceAvatars";
import { ChatTitle } from "./DocAreaChatTitle";

type DocAreaHeaderProps = {
  onNavigateBack: () => void;
};

export const DocAreaHeader: FC<DocAreaHeaderProps> = ({ onNavigateBack }) => {
  const handleGoBackToCase = () => {
    onNavigateBack();
  };

  const [caseInfoPopoverOpen, setCaseInfoPopoverOpen] = useState(false);
  const [shareCaseDialogOpen, setShareCaseDialogOpen] = useState(false);

  const { organizationId, caseId, chatId, isRestricted, isGuest } =
    useChatDocumentParams();
  const { data: caseInfo } = useGetProject(organizationId, caseId);
  const { project: parentCase, isMaster: isMasterCase } = useParentCase();
  const { data: chatInfo, isLoading } = useGetChat(
    organizationId,
    caseId,
    chatId
  );
  const { data: doclist } = useGetDoclist(organizationId, caseId);

  const totalPages = doclist?.tree.reduce((acc, item) => {
    return acc + item.category.pageCount;
  }, 0);

  return (
    <div className="w-full bg-white border-b border-zinc-200 justify-between items-center inline-flex h-full">
      <div
        className={`grow shrink basis-0 p-3 justify-between items-center flex w-full`}
      >
        <div className="justify-start items-center gap-3 flex w-full">
          {!isGuest && (
            <div className="h-8 w-8">
              <Button
                variant="secondary"
                size="icon"
                onClick={handleGoBackToCase}
                className="!h-8 !w-8 !p-0"
              >
                <ChevronLeftIcon className="h-[12.8px] w-[12.8px]" />
              </Button>
            </div>
          )}
          <div
            className={`justify-between items-center gap-2 flex ${isRestricted ? "w-full" : "w-[calc(100%-32px)]"}`}
          >
            <div className="bg-white/0 justify-start items-center gap-2 flex w-full">
              <div
                className="truncate cursor-pointer max-w-[calc(100%)] inline-block text-zinc-900 text-lg font-semibold font-inter leading-7"
                onClick={() => setCaseInfoPopoverOpen(!caseInfoPopoverOpen)}
              >
                {isLoading ? (
                  <Skeleton className="h-4 w-[120px]" />
                ) : (
                  <ChatTitle
                    parentCase={parentCase}
                    isMasterCase={isMasterCase}
                    chatTitle={chatInfo?.title ?? "Untitled Chat"}
                  />
                )}
              </div>
              {caseInfo && parentCase && chatInfo && (
                <CaseInfoPopover
                  caseInfo={parentCase}
                  chatInfo={chatInfo}
                  open={caseInfoPopoverOpen}
                  totalPages={totalPages}
                  onOpenChange={setCaseInfoPopoverOpen}
                  isMasterCase={isMasterCase}
                />
              )}
              {isRestricted && (
                <div className="ml-auto flex items-center gap-2  justify-end">
                  {isGuest && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open("/register", "_blank")}
                      className="text-blue-600 hover:text-blue-70"
                    >
                      <FormattedMessage
                        id="docList.getFullAccess"
                        defaultMessage="Get Full Access"
                      />
                    </Button>
                  )}
                  <Badge className="w-[81px] bg-blue-600 text-white rounded-full cursor-default hover:bg-blue-600">
                    <FormattedMessage
                      id="docList.viewOnly"
                      defaultMessage="View Only"
                    />
                  </Badge>
                </div>
              )}
              {!isGuest && <PresenceAvatars />}
              {!isRestricted && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShareCaseDialogOpen(true)}
                  className="ml-auto text-blue-600 hover:text-blue-700 flex items-center gap-2"
                >
                  <Share2Icon className="h-4 w-4" />
                  <span className="hidden md:inline">
                    <FormattedMessage
                      id="docList.share"
                      defaultMessage="Share"
                    />
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <ShareCaseDialog
        open={shareCaseDialogOpen}
        onOpenChange={setShareCaseDialogOpen}
        organizationId={organizationId}
        caseId={caseId}
        chatId={chatId}
        caseName={caseInfo?.name || ""}
      />
    </div>
  );
};
